import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, LogIn } from 'lucide-react';
import { isValidRoomCode, isValidParticipantName } from '../../utils/validators';
import { joinSession } from '../../services/participantService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

export default function JoinScreen({ onJoined }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawSession = searchParams.get('session') || '';
  const prefilledSession = rawSession && rawSession !== 'undefined' && rawSession !== 'null' ? rawSession : '';

  const [step, setStep] = useState(prefilledSession ? 'name' : 'pin');
  const [roomCode, setRoomCode] = useState(prefilledSession);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRoomCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) {
      setRoomCode(value);
      setError('');
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 20) {
      setName(value);
      setError('');
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (!isValidRoomCode(roomCode)) {
      setError('PIN må vere nøyaktig 6 siffer');
      return;
    }
    setStep('name');
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidParticipantName(name)) {
      setError('Namn må vere mellom 2 og 20 teikn');
      return;
    }

    setLoading(true);

    try {
      const participant = await joinSession(roomCode, name);
      const joinData = {
        sessionId: roomCode,
        participantId: participant.id,
        participantName: participant.name,
        joinedAt: Date.now(),
      };
      try {
        localStorage.setItem('mikkelkviss_participant', JSON.stringify(joinData));
      } catch { /* localStorage might be unavailable */ }
      onJoined(joinData);
    } catch (err) {
      try {
        const rebusRef = doc(db, 'rebusSessions', roomCode);
        const rebusSnap = await getDoc(rebusRef);
        if (rebusSnap.exists()) {
          navigate(`/mikkelchase/join?pin=${roomCode}`);
          return;
        }
      } catch {
        // Ignore lookup error, show original error
      }

      if (err.message?.includes('not found') || err.message?.includes('No session') || err.message?.includes('Fann ikkje')) {
        setError('Fann ikkje spelet. Sjekk PIN-koden og prøv igjen.');
      } else {
        setError(err.message || 'Klarte ikkje å bli med. Prøv igjen.');
      }
      setLoading(false);
    }
  };

  // Step 1: PIN input
  if (step === 'pin') {
    return (
      <div className="relative min-h-screen w-full flex flex-col items-center bg-mp-bg-lightgreen font-body p-6">
        <header className="w-full max-w-[480px] flex justify-between items-center mb-6">
          <span className="mp-eyebrow">Bli med · Mikkel kviss</span>
          <div className="w-12 h-12 rounded-full bg-white shadow-mp-1 flex items-center justify-center overflow-hidden">
            <img src="/Mikkel tenker uten bakgrunn.png" alt="" className="w-14 h-auto -mt-1" />
          </div>
        </header>

        <main className="flex-1 w-full max-w-[360px] flex flex-col items-center justify-center text-center pt-4 pb-10">
          <span className="inline-flex items-center gap-1.5 mb-3.5 bg-white border border-[rgba(17,73,66,0.12)] rounded-full font-display text-[12px] text-mp-stone-700 pr-2.5 py-1 pl-1">
            <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-mp-magenta text-white text-[12px]">1</span>
            Skriv inn PIN
          </span>
          <h1 className="font-display text-mp-darkgreen text-[40px] mb-3">Heisann!</h1>
          <p className="font-body text-mp-stone-700 text-[16px] mb-8 max-w-[300px]">
            Skriv inn dei seks sifra som verten har vist på storskjermen.
          </p>

          <form onSubmit={handlePinSubmit} className="w-full">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={roomCode}
              onChange={handleRoomCodeChange}
              placeholder="• • • • • •"
              className="w-full px-6 py-[22px] rounded-full border-2 border-mp-teal bg-white font-display text-[32px] text-mp-darkgreen text-center tracking-[0.15em] outline-none focus:border-mp-magenta focus:ring-4 focus:ring-[rgba(163,0,97,0.12)] transition-all"
              disabled={loading}
              autoComplete="off"
              autoFocus
            />

            {error && (
              <div className="mt-4 bg-[rgba(163,0,97,0.06)] border border-[rgba(163,0,97,0.20)] text-mp-magenta rounded-xl px-4 py-3 font-display text-sm">
                {error}
              </div>
            )}

            <button type="submit" className="mp-cta mp-cta-large w-full mt-6">
              <span>Gå vidare</span>
              <ArrowRight size={20} />
            </button>
          </form>

          <p className="mt-8 text-mp-stone-700 text-[13px]">
            Lag din eigen kviss på <strong className="text-mp-magenta">mikkelparken.no/kviss</strong>
          </p>
        </main>
      </div>
    );
  }

  // Step 2: Name input
  return (
    <div className="relative min-h-screen w-full flex flex-col items-center bg-mp-bg-pink font-body p-6">
      <header className="w-full max-w-[480px] flex justify-between items-center mb-6">
        <span className="mp-eyebrow">Bli med · Mikkel kviss</span>
        <span className="inline-flex items-center gap-1.5 bg-white border border-[rgba(17,73,66,0.12)] px-3 py-1.5 rounded-full font-display text-[12px] text-mp-darkgreen">
          PIN <span className="text-mp-teal tracking-[0.12em]">{roomCode}</span>
        </span>
      </header>

      <main className="flex-1 w-full max-w-[360px] flex flex-col items-center justify-center text-center pt-2 pb-10">
        <span className="inline-flex items-center gap-1.5 mb-3.5 bg-white border border-[rgba(17,73,66,0.12)] rounded-full font-display text-[12px] text-mp-stone-700 pr-2.5 py-1 pl-1">
          <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-mp-magenta text-white text-[12px]">2</span>
          Kallenamn
        </span>
        <h1 className="font-display text-mp-darkgreen text-[40px] mb-2">Nesten klar!</h1>
        <p className="font-body text-mp-stone-700 text-[16px] mb-7">Kva skal me kalle deg på storskjermen?</p>

        <form onSubmit={handleNameSubmit} className="w-full">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="Kallenamnet ditt"
            maxLength={20}
            className="w-full px-6 py-[22px] rounded-full border-2 border-mp-teal bg-white font-display text-[28px] text-mp-darkgreen text-center outline-none focus:border-mp-magenta focus:ring-4 focus:ring-[rgba(163,0,97,0.12)] transition-all"
            disabled={loading}
            autoComplete="off"
            autoFocus
          />

          {error && (
            <div className="mt-4 bg-[rgba(163,0,97,0.06)] border border-[rgba(163,0,97,0.20)] text-mp-magenta rounded-xl px-4 py-3 font-display text-sm">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="mp-cta mp-cta-large w-full mt-6">
            <span>{loading ? 'Koplar til…' : 'Bli med'}</span>
            {!loading && <LogIn size={20} />}
          </button>

          <button
            type="button"
            onClick={() => {
              setStep('pin');
              setError('');
            }}
            className="mt-4 text-mp-darkgreen text-[13px] font-display opacity-70 hover:opacity-100 transition-opacity"
          >
            ← Tilbake til PIN
          </button>
        </form>
      </main>

      <img
        src="/Mikkel tenker uten bakgrunn.png"
        alt=""
        className="fixed bottom-2 left-2 w-[90px] h-auto opacity-55 -rotate-6 pointer-events-none"
      />
    </div>
  );
}
